import { useState } from "react";
import SecretFindingOverrideDialog from "./SecretFindingOverrideDialog.jsx";

const SUCCESSFUL_IMPORT_STATUSES = new Set(["imported", "already_imported"]);

function namesFromUnresolved(entries) {
    return [...new Set((entries || [])
        .map((entry) => typeof entry === "string" ? entry : entry?.command)
        .filter(Boolean))];
}

export function migrationStatusSummary(report) {
    const messages = [];
    const results = [...(report?.kernels || []), ...(report?.canvases || [])];
    const waiting = results.filter((result) => result.status === "waiting_for_dependencies");
    const waitingCommands = namesFromUnresolved(waiting.flatMap(
        (result) => result.unresolved || [],
    ));
    if (waiting.length > 0) {
        messages.push(
            `Locally saved canvases are waiting for kernel dependencies${
                waitingCommands.length ? `: ${waitingCommands.join(", ")}` : ""
            }.`,
        );
    }

    const pending = results.filter((result) => result.status === "pending_cloud_create");
    if (pending.length > 0) {
        messages.push("Some locally saved Studio items have unfinished cloud saves. Reload to retry recovery.");
    }

    const committed = results.filter((result) => result.status === "committed");
    if (committed.length > 0) {
        messages.push("Some Studio items reached cloud, but their local recovery records could not be finalized.");
    }

    const otherFailures = results.filter((result) =>
        !SUCCESSFUL_IMPORT_STATUSES.has(result.status)
        && ![
            "approval_required",
            "committed",
            "pending_cloud_create",
            "rejected",
            "waiting_for_dependencies",
        ].includes(result.status));
    if (otherFailures.length > 0) {
        messages.push("Some locally saved Studio items could not be migrated. Reload to retry.");
    }

    const pinCommands = namesFromUnresolved(report?.pins?.unresolved);
    if (report?.pins?.status === "incomplete") {
        if (report?.cloudStatus && report.cloudStatus !== "ready") {
            messages.push("Legacy kernel pins could not be restored because the cloud kernel catalog is unavailable.");
        } else {
            messages.push(
                `Legacy kernel pins could not be restored because matching kernels were not found${
                    pinCommands.length ? `: ${pinCommands.join(", ")}` : ""
                }.`,
            );
        }
    } else if (["invalid", "unavailable"].includes(report?.pins?.status)) {
        messages.push("Legacy kernel pins could not be read or saved. They were left unchanged.");
    }

    if ([report?.kernelBucket, report?.canvasBucket]
        .some((bucket) => ["invalid", "unavailable"].includes(bucket?.status))) {
        messages.push("Some locally saved Studio data could not be read. It was left unchanged.");
    }
    if ([report?.kernelCleanup, report?.canvasCleanup]
        .some((cleanup) => ["invalid", "unavailable"].includes(cleanup?.status))) {
        messages.push("Cloud migration finished, but some local recovery data could not be cleaned up.");
    }
    if (messages.length === 0) {
        return "Studio migration needs attention. Locally saved data was left unchanged.";
    }
    return messages.join(" ");
}

export default function TenantViewStartupNotice({
    report,
    onApproveSecrets,
    onDismiss,
}) {
    const [reviewing, setReviewing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [approvalError, setApprovalError] = useState("");
    if (!report || report.status === "complete") return null;
    const approvalRequired = [
        ...(report.kernels || []),
        ...(report.canvases || []),
    ].find((result) =>
        result.status === "approval_required" && result.secretInspection);
    const rejected = [...(report.kernels || []), ...(report.canvases || [])]
        .filter((result) => result.status === "rejected");
    const rejectedSummary = rejected
        .slice(0, 3)
        .map((result) => {
            const name = result.mapping?.name || result.localId || "unnamed item";
            const reason = result.mapping?.rejectionMessage
                || result.error?.serverMessage
                || result.error?.message
                || "the server rejected it";
            return `${name}: ${reason}`;
        })
        .join("; ");

    const confirmApproval = async (secretOverrides) => {
        if (!approvalRequired || typeof onApproveSecrets !== "function") return;
        setBusy(true);
        setApprovalError("");
        try {
            await onApproveSecrets(approvalRequired.approvalKey, secretOverrides);
            setReviewing(false);
        } catch (error) {
            setApprovalError(error?.message || "Cloud import failed. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div
                role="alert"
                className="mb-[8px] flex items-start justify-between gap-[12px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]"
            >
                <span>
                    {approvalRequired ? (
                        <>
                            A locally saved Studio item contains secret-like text and was kept
                            private on this device. Review the categories and paths before
                            importing it to cloud.
                        </>
                    ) : rejected.length > 0 ? (
                        <>
                            Some locally saved Studio items were rejected by cloud and were kept locally.{" "}
                            {rejectedSummary}
                            {rejected.length > 3 ? `; and ${rejected.length - 3} more` : ""}.
                            Edit a rejected item to retry it; unchanged items will not be retried.
                        </>
                    ) : (
                        migrationStatusSummary(report)
                    )}
                </span>
                <div className="flex shrink-0 items-center gap-[8px]">
                    {approvalRequired && onApproveSecrets ? (
                        <button
                            type="button"
                            onClick={() => {
                                setApprovalError("");
                                setReviewing(true);
                            }}
                            className="rounded-[2px] border border-[color:var(--line)] px-[7px] py-[3px] text-[10px] uppercase tracking-[0.04em] hover:text-[color:var(--ink)]"
                        >
                            Review and import
                        </button>
                    ) : null}
                    {onDismiss ? (
                        <button
                            type="button"
                            onClick={onDismiss}
                            aria-label="Dismiss Studio migration warning"
                            className="bg-transparent text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                        >
                            ×
                        </button>
                    ) : null}
                </div>
            </div>
            {reviewing && approvalRequired ? (
                <SecretFindingOverrideDialog
                    title="Review local migration findings"
                    description="The local item contains secret-like text. Only categories and paths are shown here; approve every false positive before retaining the content in cloud."
                    confirmLabel="Approve all and import"
                    busyLabel="Importing…"
                    findings={approvalRequired.secretInspection.findings}
                    contentDigest={approvalRequired.secretInspection.contentDigest}
                    detectorVersion={approvalRequired.secretInspection.detectorVersion}
                    busy={busy}
                    errorMessage={approvalError}
                    onCancel={() => setReviewing(false)}
                    onConfirm={confirmApproval}
                />
            ) : null}
        </>
    );
}
